import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LayoutDashboard, Shield, LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { label: "How It Works", to: "/#how-it-works" },
  { label: "What You Receive", to: "/#benefits" },
  { label: "Plans", to: "/#pricing" },
  { label: "Consult an Astrologer", to: "/#consult" },
  { label: "Lucky Draw", to: "/#lucky-draw" },
  { label: "FAQ", to: "/#faq" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goHash = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (loc.pathname !== "/") {
        nav("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 200);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else nav(to);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-amber-500/15 py-3" : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => goHash("/")} className="shrink-0">
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
          {NAV.map((n) => (
            <button
              key={n.label}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => goHash(n.to)}
              className="relative text-sm text-slate-300 hover:text-amber-200 transition-colors after:absolute after:left-0 after:-bottom-1 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-amber-300/70 after:transition-transform hover:after:scale-x-100"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              {user.role === "admin" && (
                <button
                  data-testid="nav-admin-link"
                  onClick={() => nav("/admin")}
                  className="btn-ghost-gold rounded-full px-4 py-2 text-sm flex items-center gap-2"
                >
                  <Shield size={15} /> Admin
                </button>
              )}
              <button
                data-testid="nav-dashboard-link"
                onClick={() => nav("/dashboard")}
                className="btn-ghost-gold rounded-full px-4 py-2 text-sm flex items-center gap-2"
              >
                <LayoutDashboard size={15} /> Dashboard
              </button>
              <button
                data-testid="nav-logout-btn"
                onClick={async () => { await logout(); nav("/"); }}
                className="text-slate-400 hover:text-amber-200 p-2"
                title="Log out"
              >
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="nav-signin-btn"
                onClick={() => nav("/login")}
                className="text-sm text-slate-200 hover:text-amber-200 px-3 py-2"
              >
                Sign In
              </button>
              <button
                data-testid="nav-getstarted-btn"
                onClick={() => nav("/register")}
                className="btn-gold rounded-full px-5 py-2.5 text-sm"
              >
                Start – ₹499/mo
              </button>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-slate-100 p-2"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden glass border-t border-amber-500/15 mt-3">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <button
                key={n.label}
                data-testid={`mnav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => goHash(n.to)}
                className="text-left text-slate-200 py-1.5 hover:text-amber-200 transition-colors"
              >
                {n.label}
              </button>
            ))}
            <div className="hairline pt-3 flex flex-col gap-2">
              {user ? (
                <>
                  {user.role === "admin" && (
                    <button onClick={() => { setOpen(false); nav("/admin"); }} className="btn-ghost-gold rounded-full px-4 py-2.5 text-sm">Admin Panel</button>
                  )}
                  <button onClick={() => { setOpen(false); nav("/dashboard"); }} className="btn-gold rounded-full px-4 py-2.5 text-sm">Dashboard</button>
                  <button onClick={async () => { setOpen(false); await logout(); nav("/"); }} className="text-slate-400 text-sm py-1">Log out</button>
                </>
              ) : (
                <>
                  <button onClick={() => { setOpen(false); nav("/login"); }} className="btn-ghost-gold rounded-full px-4 py-2.5 text-sm">Sign In</button>
                  <button onClick={() => { setOpen(false); nav("/register"); }} className="btn-gold rounded-full px-4 py-2.5 text-sm">Start – ₹499/mo</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
